import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PropertyDetailsSnapshotEnvelope, ReportSnapshotEnvelope } from '@root/shared-report-platform/models/report-snapshot-envelope.model';
import { CreateSharedLinkRequest, CreateSharedLinkResponse } from '@root/shared-report-platform/models/shared-link.model';
import { PropertyUtil } from '@shared/utils/property-util';
import { PropertyInformation } from '@store/search-board/search-board.model';
import { defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SharedLinkApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  createSharedLink<TSnapshot extends ReportSnapshotEnvelope<unknown> = PropertyDetailsSnapshotEnvelope>(
    propertyInformation: PropertyInformation,
    envelopes: readonly TSnapshot[]
  ): Observable<CreateSharedLinkResponse> {
    return defer(() => {
      const identifier = propertyInformation?.propertyIdentifier;
      if (!identifier) {
        throw new Error('A property identifier is required to create a shared link.');
      }
      if (!envelopes.length) {
        throw new Error('At least one report snapshot is required to create a shared link.');
      }

      const propertyData = propertyInformation.propertyData;
      const request: CreateSharedLinkRequest<TSnapshot> = {
        county: identifier.fipsCode ?? '',
        address: PropertyUtil.buildAddress(propertyData, identifier).trim(),
        clip: identifier.clip ?? '',
        apn: propertyData.APN ?? identifier.apn ?? '',
        reports: envelopes.map(envelope => ({
          reportName: envelope.reportId,
          reportSnapshotJson: { ...envelope, artifacts: envelope.artifacts ?? {} }
        }))
      };

      return this.http.post<CreateSharedLinkResponse>('/api/reports/share-link', request).pipe(
        map(response => ({ ...response, url: this.resolveUrl(response.url) }))
      );
    });
  }

  private resolveUrl(url: string): string {
    const message = 'The shared link response must contain a valid HTTP(S) URL.';
    if (typeof url !== 'string' || !url.trim()) {
      throw new Error(message);
    }

    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(url.trim());
    if (isAbsolute && !/^https?:\/\/[^/\\\s]/i.test(url.trim())) {
      throw new Error(message);
    }
    let resolved: URL;
    try {
      resolved = isAbsolute ? new URL(url) : new URL(url, this.document.location.origin);
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        throw new Error(message);
      }
      throw error;
    }
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
      throw new Error(message);
    }
    return isAbsolute ? url : resolved.href;
  }
}
